import type { IAppThunk } from "../../app/store";
import { selectActivePartialMcbimSpace } from "../../McBim/mcbimSelectors";
import { selectProjectNo } from "../designSlice";
import { selectHttpDevicesInRoom } from "../devices/deviceSlice";
import { upsertUnsavedRoutingItem } from "./unsavedRoutingSlice";
import { createDemoRoomPipeRouting, IPolygon } from "@multiconsult-group/multiplacer-core";

const getDemoRoutingLocalId = (roomName: string) => `demo-room-routing:${roomName}`;
const DEMO_PIPE_HEIGHT_ABOVE_FLOOR_M = 3.5; // 3500 mm

export const thunkGenerateDemoPipeRoutingForActiveRoom = (): IAppThunk => {
  return (dispatch, getState) => {
    const state = getState();
    const activeMcbimSpace = selectActivePartialMcbimSpace(state);
    const projectNo = selectProjectNo(state);
    const floorElevation = state.design.floorAndJob?.elevation;
    const floorName = state.design.floorAndJob?.floorName;
    const roomNumber = activeMcbimSpace?.name;
    const devicesInRoom = selectHttpDevicesInRoom(state);
    const pipeElevation = (floorElevation ?? 0) + DEMO_PIPE_HEIGHT_ABOVE_FLOOR_M;

    if (!activeMcbimSpace) {
      console.warn("thunkGenerateDemoPipeRoutingForActiveRoom: No active McBIM space found");
      return;
    }
    if (!projectNo) {
      console.warn("thunkGenerateDemoPipeRoutingForActiveRoom: No projectNo found in state");
      return;
    }
    if (devicesInRoom.length === 0) {
      console.warn(
        "thunkGenerateDemoPipeRoutingForActiveRoom: No devices found in active room for demo routing anchor"
      );
      return;
    }

    const polygon = activeMcbimSpace.polygon as IPolygon | undefined;
    if (!polygon || polygon.length < 4) {
      console.warn(
        "thunkGenerateDemoPipeRoutingForActiveRoom: Active room is missing a valid polygon"
      );
      return;
    }

    const roomName = String(activeMcbimSpace.name ?? "unknown-room");
    const randomDevice = devicesInRoom[Math.floor(Math.random() * devicesInRoom.length)];
    const bbox = polygon.reduce(
      (acc, [x, y]) => ({
        minX: Math.min(acc.minX, x),
        minY: Math.min(acc.minY, y),
        maxX: Math.max(acc.maxX, x),
        maxY: Math.max(acc.maxY, y),
      }),
      {
        minX: Number.POSITIVE_INFINITY,
        minY: Number.POSITIVE_INFINITY,
        maxX: Number.NEGATIVE_INFINITY,
        maxY: Number.NEGATIVE_INFINITY,
      }
    );
    console.info("thunkGenerateDemoPipeRoutingForActiveRoom: input", {
      roomName,
      polygonPointCount: polygon.length,
      polygonPreview: polygon.slice(0, 8),
      bbox,
      bboxWidth: bbox.maxX - bbox.minX,
      bboxHeight: bbox.maxY - bbox.minY,
      expectedDemoLengthsInRoomUnits: { first: 2, second: 1 },
      projectNo,
      floorElevation,
      pipeElevation,
      floorName,
      roomNumber,
      randomDeviceId: randomDevice?._id,
    });

    let routingGraph;
    try {
      routingGraph = createDemoRoomPipeRouting(polygon, {
        projectNo,
        device: randomDevice,
        elevation: pipeElevation,
        floorName,
        roomNumber,
      });
    } catch (error) {
      console.warn("thunkGenerateDemoPipeRoutingForActiveRoom: Failed to generate demo routing", {
        roomName,
        polygonPointCount: polygon.length,
        bbox,
        error,
      });
      return;
    }

    dispatch(
      upsertUnsavedRoutingItem({
        localId: getDemoRoutingLocalId(roomName),
        routingGraph,
        createdAtIso: new Date().toISOString(),
      })
    );
  };
};
